import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  KeyRound,
  ShieldCheck,
  Trash2,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  Eye,
  EyeOff,
  Lock,
  Star,
  StarOff,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [providers, setProviders] = useState([]);
  const [saved, setSaved] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [{ data: specs }, { data: keys }, { data: act }] = await Promise.all([
        api.get("/api/byok/providers"),
        api.get("/api/byok"),
        api.get("/api/byok/active"),
      ]);
      setProviders(specs.providers || []);
      setSaved(keys || []);
      setActive(act?.active || null);
      if (!activeTab && specs.providers?.length) {
        setActiveTab(specs.providers[0].id);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const savedFor = (pid) => saved.find((k) => k.provider === pid);
  const activeProvider = providers.find((p) => p.id === activeTab);

  return (
    <div className="p-6 md:p-10 space-y-8" data-testid="settings-page">
      <div>
        <div className="eyebrow">/ workspace · settings</div>
        <h1 className="mt-2 text-3xl md:text-4xl font-medium tracking-tight">
          API Keys
        </h1>
        <p className="mt-2 text-[color:var(--text-2)] max-w-3xl">
          Bring your own credentials from any of four providers. Every field
          marked with a lock is encrypted at rest before it ever hits MongoDB.
          Pick the provider IVOROVI should use for scans right from the picker
          on the Scan page — or set a workspace-wide default here.
        </p>
      </div>

      {/* Enterprise cloud-boundary note */}
      <div
        data-testid="enterprise-note"
        className="border border-[color:var(--info)]/40 bg-[color:var(--info)]/5 p-4 flex items-start gap-3"
      >
        <ShieldCheck size={16} className="text-[color:var(--info)] mt-0.5 shrink-0" />
        <div className="text-sm text-[color:var(--text-2)]">
          <span className="text-white font-medium">Enterprise customers:</span>{" "}
          Using Azure or AWS ensures your data stays within your secure cloud
          boundary and is never used for training.
        </div>
      </div>

      {!isAdmin && (
        <div
          data-testid="admin-only-notice"
          className="border border-[color:var(--warning)]/40 bg-[color:var(--warning)]/5 p-4 flex items-start gap-3"
        >
          <AlertTriangle size={16} className="text-[color:var(--warning)] mt-0.5 shrink-0" />
          <div className="mono text-xs text-[color:var(--text-2)]">
            API keys are managed at the workspace level and can only be edited
            by an <span className="text-white">admin</span>. You have read-only
            visibility.
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border border-[color:var(--border)] bg-[color:var(--bg-1)]">
        <div className="border-b border-[color:var(--border)] flex overflow-x-auto">
          {providers.map((p) => {
            const isSaved = !!savedFor(p.id);
            const isActive = activeTab === p.id;
            const isWorkspaceDefault = active === p.id;
            return (
              <button
                key={p.id}
                data-testid={`provider-tab-${p.id}`}
                onClick={() => setActiveTab(p.id)}
                className={`flex items-center gap-2 px-5 py-3 mono text-xs uppercase tracking-widest border-b-2 whitespace-nowrap transition-colors duration-150 ${
                  isActive
                    ? "border-[color:var(--accent)] text-white bg-[color:var(--bg-2)]"
                    : "border-transparent text-[color:var(--text-2)] hover:text-white"
                }`}
              >
                {p.label}
                {isSaved && (
                  <span
                    className="w-1.5 h-1.5 rounded-full bg-[color:var(--success)]"
                    title="Credentials saved"
                  />
                )}
                {isWorkspaceDefault && (
                  <Star size={11} className="text-[color:var(--accent)]" />
                )}
              </button>
            );
          })}
        </div>
        {activeProvider && (
          <ProviderPanel
            provider={activeProvider}
            saved={savedFor(activeProvider.id)}
            isWorkspaceDefault={active === activeProvider.id}
            editable={isAdmin}
            loading={loading}
            onChanged={loadAll}
          />
        )}
      </div>

      <div className="border border-[color:var(--border)] bg-[color:var(--bg-1)] p-5 space-y-2">
        <div className="eyebrow flex items-center gap-2">
          <Lock size={12} /> security notes
        </div>
        <ul className="mono text-xs text-[color:var(--text-2)] space-y-1 list-disc pl-5">
          <li>All keys and secrets are encrypted with Fernet (AES-128-CBC + HMAC) before hitting MongoDB.</li>
          <li>Config fields (endpoint, region, deployment name, model id) are stored in plaintext — no secret material.</li>
          <li>The UI only ever shows the last 4 characters of the primary secret.</li>
          <li>Test-connection calls use the cheapest available vendor endpoint (token count, list-models, list-foundation-models).</li>
          <li>Deleting a provider that was set as workspace default automatically clears the default.</li>
        </ul>
      </div>
    </div>
  );
}

function ProviderPanel({ provider, saved, isWorkspaceDefault, editable, loading, onChanged }) {
  const [values, setValues] = useState({});
  const [reveal, setReveal] = useState({});
  const [validating, setValidating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [settingDefault, setSettingDefault] = useState(false);

  // Reset local form state when tab changes / saved state changes
  useEffect(() => {
    setValues({});
    setReveal({});
  }, [provider.id]);

  const secretFields = useMemo(() => provider.fields.filter((f) => f.secret), [provider]);

  const buildPayload = () => {
    const config = {};
    const secrets = {};
    for (const f of provider.fields) {
      const v = (values[f.name] ?? "").trim();
      if (!v) continue;
      if (f.secret) secrets[f.name] = v;
      else config[f.name] = v;
    }
    return { provider: provider.id, config, secrets };
  };

  const hasAllRequired = () =>
    provider.fields.every((f) => !f.required || (values[f.name] ?? "").trim());

  const validate = async () => {
    if (!hasAllRequired()) {
      toast.error("Fill in every required field first");
      return;
    }
    setValidating(true);
    try {
      await api.post("/api/byok/validate", buildPayload());
      toast.success(`${provider.label} credentials valid`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setValidating(false);
    }
  };

  const save = async () => {
    if (!hasAllRequired()) {
      toast.error("Fill in every required field first");
      return;
    }
    setSaving(true);
    try {
      await api.post("/api/byok", buildPayload());
      toast.success(`${provider.label} credentials saved`);
      setValues({});
      setReveal({});
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete the saved ${provider.label} credentials?`)) return;
    setDeleting(true);
    try {
      await api.delete(`/api/byok/${provider.id}`);
      toast.success(`${provider.label} credentials removed`);
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setDeleting(false);
    }
  };

  const toggleDefault = async () => {
    setSettingDefault(true);
    try {
      await api.put("/api/byok/active", {
        provider: isWorkspaceDefault ? null : provider.id,
      });
      toast.success(
        isWorkspaceDefault
          ? "Workspace default cleared"
          : `${provider.label} set as workspace default`,
      );
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setSettingDefault(false);
    }
  };

  return (
    <div className="p-6 space-y-5" data-testid={`provider-panel-${provider.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-lg font-medium tracking-tight flex items-center gap-2">
            {provider.label}
            {isWorkspaceDefault && (
              <span className="mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-[color:var(--accent)]/40 text-[color:var(--accent)]">
                workspace default
              </span>
            )}
          </div>
          <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)] mt-1">
            {provider.hint}
          </div>
        </div>
        {saved ? (
          <span
            data-testid={`byok-status-${provider.id}`}
            className="mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-[color:var(--success)]/40 text-[color:var(--success)]"
          >
            connected · •••{saved.primary_last4}
          </span>
        ) : (
          <span
            data-testid={`byok-status-${provider.id}`}
            className="mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-[color:var(--border-strong)] text-[color:var(--text-3)]"
          >
            not configured
          </span>
        )}
      </div>

      {saved && editable && (
        <div className="border border-[color:var(--border)] bg-[color:var(--bg-2)] p-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="mono text-xs text-[color:var(--text-2)]">
            saved{" "}
            <span className="text-[color:var(--text-3)]">
              {new Date(saved.created_at).toLocaleString()}
            </span>
          </div>
          <div className="flex gap-2">
            <button
              data-testid={`byok-default-${provider.id}`}
              onClick={toggleDefault}
              disabled={settingDefault}
              className="inline-flex items-center gap-2 border border-[color:var(--border-strong)] h-9 px-3 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition-colors duration-150 mono text-[10px] uppercase tracking-widest disabled:opacity-50"
            >
              {settingDefault ? <Loader2 size={12} className="animate-spin" /> : isWorkspaceDefault ? <StarOff size={12} /> : <Star size={12} />}
              {isWorkspaceDefault ? "clear default" : "set as default"}
            </button>
            <button
              data-testid={`byok-delete-${provider.id}`}
              onClick={remove}
              disabled={deleting}
              className="inline-flex items-center gap-2 border border-[color:var(--border-strong)] h-9 px-3 hover:border-[color:var(--critical)] hover:text-[color:var(--critical)] transition-colors duration-150 mono text-[10px] uppercase tracking-widest disabled:opacity-50"
            >
              {deleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
              remove
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="mono text-xs text-[color:var(--text-3)] flex items-center gap-2">
          <Loader2 size={12} className="animate-spin" /> loading…
        </div>
      ) : editable ? (
        <div className="space-y-3">
          <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)]">
            {saved ? "replace credentials" : "add credentials"}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {provider.fields.map((f) => (
              <FieldInput
                key={f.name}
                field={f}
                value={values[f.name] || ""}
                reveal={!!reveal[f.name]}
                onChange={(v) => setValues((s) => ({ ...s, [f.name]: v }))}
                onToggleReveal={() => setReveal((s) => ({ ...s, [f.name]: !s[f.name] }))}
                testId={`byok-input-${provider.id}-${f.name}`}
              />
            ))}
          </div>
          <div className="flex flex-wrap gap-2 pt-1">
            <button
              data-testid={`byok-validate-${provider.id}`}
              onClick={validate}
              disabled={validating || !hasAllRequired()}
              className="inline-flex items-center gap-2 border border-[color:var(--border-strong)] h-11 px-4 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition-colors duration-150 mono text-xs uppercase tracking-widest disabled:opacity-50"
            >
              {validating ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
              Test Connection
            </button>
            <button
              data-testid={`byok-save-${provider.id}`}
              onClick={save}
              disabled={saving || !hasAllRequired()}
              className="inline-flex items-center gap-2 bg-[color:var(--accent)] text-black h-11 px-5 hover:bg-[color:var(--accent-hover)] transition-colors duration-150 mono text-xs uppercase tracking-widest disabled:opacity-50"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <KeyRound size={12} />}
              Validate &amp; Save
            </button>
          </div>
        </div>
      ) : (
        <div className="mono text-xs text-[color:var(--text-3)]">
          Ask a workspace admin to add credentials.
        </div>
      )}
    </div>
  );
}

function FieldInput({ field, value, onChange, reveal, onToggleReveal, testId }) {
  const type = field.secret && !reveal ? "password" : field.kind === "url" ? "url" : "text";
  return (
    <div>
      <label className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)] flex items-center gap-1.5">
        {field.secret && <Lock size={10} />}
        {field.label}
      </label>
      <div className="relative mt-1">
        <input
          data-testid={testId}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          autoComplete="off"
          spellCheck={false}
          className="w-full h-11 bg-[color:var(--bg-base)] border border-[color:var(--border)] px-3 pr-10 mono text-sm text-white placeholder:text-[color:var(--text-3)] focus:border-[color:var(--accent)] focus:outline-none focus:ring-1 focus:ring-[color:var(--accent)] transition-colors duration-150"
        />
        {field.secret && (
          <button
            type="button"
            data-testid={`${testId}-reveal`}
            onClick={onToggleReveal}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[color:var(--text-3)] hover:text-white transition-colors duration-150"
            tabIndex={-1}
          >
            {reveal ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        )}
      </div>
    </div>
  );
}
